import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { RefObject, useRef } from 'react';
import { useSmootherReady } from '@/hooks/useSmootherReady';
import { gsap, ScrollTrigger } from '@/lib/gsap';
import { DESKTOP_MEDIA_QUERY, SCROLL_HEIGHT_PER_SLIDE, SLIDE_WHEEL_COOLDOWN_MS } from '../const';
import { animateSlideChange, setInitialSlideState } from '../utils/animateSlideChange';
import { scrollToSlideIndex } from '../utils/scrollToSlideIndex';
interface SlideRefs {
  mediaItems: RefObject<(HTMLDivElement | null)[]>;
  rightItems: RefObject<(HTMLDivElement | null)[]>;
  tabItems: RefObject<(HTMLLIElement | null)[]>;
}
interface Params {
  sectionRef: RefObject<HTMLElement | null>;
  pinRef: RefObject<HTMLDivElement | null>;
  slideRefs: SlideRefs;
  slidesCount: number;
  enabled: boolean;
  onActiveIndexChange: (index: number) => void;
  pinStart?: string;
}
export const useCasesSliderPinScroll = t0 => {
  const $ = _c(18);
  const {
    sectionRef,
    pinRef,
    slideRefs,
    slidesCount,
    enabled,
    onActiveIndexChange,
    pinStart: t1
  } = t0;
  const pinStart = t1 === undefined ? "top top" : t1;
  const smoother = useSmootherReady();
  const scrollTriggerRef = useRef(null);
  const activeIndexRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const isCooldownRef = useRef(false);
  const cooldownTweenRef = useRef(null);
  let t2;
  if ($[0] !== enabled || $[1] !== onActiveIndexChange || $[2] !== pinRef || $[3] !== pinStart || $[4] !== sectionRef || $[5] !== slideRefs || $[6] !== slidesCount || $[7] !== smoother) {
    t2 = () => {
      if (!smoother || !enabled || !sectionRef.current || !pinRef.current || slidesCount <= 1) {
        return;
      }
      const sectionEl = sectionRef.current;
      const pinEl = pinRef.current;
      const mm = gsap.matchMedia();
      const cleanups = [];
      const refreshPinTrigger = () => scrollTriggerRef.current?.refresh();
      mm.add(DESKTOP_MEDIA_QUERY, () => {
        activeIndexRef.current = 0;
        isTransitioningRef.current = false;
        isCooldownRef.current = false;
        onActiveIndexChange(0);
        const refs = {
          mediaItems: slideRefs.mediaItems.current,
          rightItems: slideRefs.rightItems.current,
          tabItems: slideRefs.tabItems.current
        };
        setInitialSlideState(refs, slidesCount);
        const scrollDistance = () => window.innerHeight * SCROLL_HEIGHT_PER_SLIDE * Math.max(slidesCount - 1, 1);
        const isLocked = () => isTransitioningRef.current || isCooldownRef.current;
        const startCooldown = () => {
          isCooldownRef.current = true;
          cooldownTweenRef.current?.kill();
          cooldownTweenRef.current = gsap.delayedCall(SLIDE_WHEEL_COOLDOWN_MS / 1000, () => {
            isCooldownRef.current = false;
          });
        };
        const goToSlide = (nextIndex, t3) => {
          const scrollBehavior = t3 === undefined ? "auto" : t3;
          if (nextIndex === activeIndexRef.current || nextIndex < 0 || nextIndex >= slidesCount) {
            return;
          }
          if (isLocked()) {
            return;
          }
          const prevIndex = activeIndexRef.current;
          isTransitioningRef.current = true;
          cooldownTweenRef.current?.kill();
          isCooldownRef.current = false;
          const tl = animateSlideChange({
            refs,
            prevIndex,
            nextIndex,
            onIndexChange: index => {
              activeIndexRef.current = index;
              onActiveIndexChange(index);
            }
          });
          tl.eventCallback("onComplete", () => {
            isTransitioningRef.current = false;
            startCooldown();
            const trigger = scrollTriggerRef.current;
            if (trigger) {
              scrollToSlideIndex(trigger, nextIndex, slidesCount, scrollBehavior);
            }
          });
        };
        const st = ScrollTrigger.create({
          trigger: pinEl,
          start: pinStart,
          end: () => `+=${scrollDistance()}`,
          pin: pinEl,
          invalidateOnRefresh: true
        });
        scrollTriggerRef.current = st;
        const layoutRefreshId = requestAnimationFrame(() => {
          requestAnimationFrame(refreshPinTrigger);
        });
        cleanups.push(() => cancelAnimationFrame(layoutRefreshId));
        const approachSt = ScrollTrigger.create({
          trigger: sectionEl,
          start: "top bottom",
          once: true,
          onEnter: refreshPinTrigger
        });
        cleanups.push(() => approachSt.kill());
        document.fonts?.ready?.then(refreshPinTrigger);
        const handleWheel = e => {
          if (!st.isActive) {
            return;
          }
          if (isLocked()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
          }
          const direction = Math.sign(e.deltaY);
          if (!direction) {
            return;
          }
          const current = activeIndexRef.current;
          const next = current + direction;
          if (next < 0 || next >= slidesCount) {
            return;
          }
          e.preventDefault();
          e.stopImmediatePropagation();
          goToSlide(next);
        };
        window.addEventListener("wheel", handleWheel, {
          passive: false,
          capture: true
        });
        cleanups.push(() => window.removeEventListener("wheel", handleWheel, {
          capture: true
        }));
        slideRefs.tabItems.current.forEach((item, idx) => {
          if (!item) {
            return;
          }
          const goToSlideFromTab = () => goToSlide(idx, "smooth");
          const onKeyDown = e_0 => {
            if (e_0.key === "Enter" || e_0.key === " ") {
              e_0.preventDefault();
              goToSlideFromTab();
            }
          };
          item.addEventListener("click", goToSlideFromTab);
          item.addEventListener("keydown", onKeyDown);
          cleanups.push(() => {
            item.removeEventListener("click", goToSlideFromTab);
            item.removeEventListener("keydown", onKeyDown);
          });
        });
        return () => {
          cleanups.forEach(_temp);
          cooldownTweenRef.current?.kill();
          scrollTriggerRef.current = null;
          isTransitioningRef.current = false;
          isCooldownRef.current = false;
          st.kill();
        };
      });
      return () => {
        cleanups.forEach(_temp2);
        cooldownTweenRef.current?.kill();
        scrollTriggerRef.current = null;
        mm.revert();
      };
    };
    $[0] = enabled;
    $[1] = onActiveIndexChange;
    $[2] = pinRef;
    $[3] = pinStart;
    $[4] = sectionRef;
    $[5] = slideRefs;
    $[6] = slidesCount;
    $[7] = smoother;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== enabled || $[10] !== pinStart || $[11] !== slidesCount || $[12] !== smoother) {
    t3 = [slidesCount, enabled, smoother, pinStart];
    $[9] = enabled;
    $[10] = pinStart;
    $[11] = slidesCount;
    $[12] = smoother;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== sectionRef || $[15] !== t3) {
    t4 = {
      scope: sectionRef,
      dependencies: t3
    };
    $[14] = sectionRef;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  useGSAP(t2, t4);
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      scrollTriggerRef
    };
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
function _temp(cleanup) {
  return cleanup();
}
function _temp2(cleanup_0) {
  return cleanup_0();
}